import type { SVSColor } from './svs-types'

/**
 * Maps SVS semantic color tokens and named colors to concrete CSS values.
 * Uses HSL values from the design system where possible.
 */

const semanticMap: Record<string, string> = {
  positive: 'hsl(142, 71%, 45%)',   // green-500
  negative: 'hsl(0, 84%, 60%)',     // red-500
  neutral: 'hsl(215, 14%, 57%)',    // slate-400
  warning: 'hsl(38, 92%, 50%)',     // amber-500
  info: 'hsl(217, 91%, 60%)',       // blue-500
  primary: 'hsl(221, 83%, 53%)',    // blue-600
  secondary: 'hsl(262, 83%, 58%)', // violet-500
  muted: 'hsl(215, 16%, 72%)',      // slate-300
}

const namedMap: Record<string, string> = {
  red: 'hsl(0, 84%, 60%)',
  blue: 'hsl(217, 91%, 60%)',
  green: 'hsl(142, 71%, 45%)',
  amber: 'hsl(38, 92%, 50%)',
  teal: 'hsl(173, 80%, 40%)',
  purple: 'hsl(262, 83%, 58%)',
  orange: 'hsl(25, 95%, 53%)',
  gray: 'hsl(215, 14%, 57%)',
}

/**
 * Default palette used when no color is specified on data items.
 * Ordered for maximum visual distinction.
 */
export const defaultPalette = [
  'hsl(221, 83%, 53%)',  // blue
  'hsl(25, 95%, 53%)',   // orange
  'hsl(142, 71%, 45%)',  // green
  'hsl(262, 83%, 58%)',  // purple
  'hsl(38, 92%, 50%)',   // amber
  'hsl(173, 80%, 40%)',  // teal
  'hsl(0, 84%, 60%)',    // red
  'hsl(215, 14%, 57%)',  // gray
  'hsl(340, 82%, 52%)',  // pink
  'hsl(47, 96%, 53%)',   // yellow
]

/**
 * Resolve an SVS color value to a concrete CSS color string.
 * Falls back to a palette color by index if not specified.
 */
export function resolveColor(color: SVSColor | undefined, fallbackIndex: number = 0): string {
  if (!color) return defaultPalette[fallbackIndex % defaultPalette.length]

  // Semantic token
  if (color in semanticMap) return semanticMap[color]

  // Named color
  if (color in namedMap) return namedMap[color]

  // Hex code or other CSS color — pass through
  if (color.startsWith('#') || color.startsWith('hsl') || color.startsWith('rgb')) return color

  // Unknown — use palette fallback
  return defaultPalette[fallbackIndex % defaultPalette.length]
}

/**
 * Map an SVS sentiment to a Tailwind text color class.
 */
export function sentimentTextClass(sentiment: 'positive' | 'negative' | 'neutral'): string {
  switch (sentiment) {
    case 'positive':
      return 'text-green-600 dark:text-green-400'
    case 'negative':
      return 'text-red-600 dark:text-red-400'
    case 'neutral':
      return 'text-muted-foreground'
  }
}
